import { AdminRoute } from "@features/auth/presentation/components/AdminRoute";
import { ProtectedRoute } from "@features/auth/presentation/components/ProtectedRoute";
import LoginPage from "@features/auth/presentation/page/LoginPage";
import { LoadingScreen } from "@shared/components/molecules/LoadingScreen/LoadingScreen";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Lazy loading con retry + auto-reload si el chunk ya no existe (deploy nuevo)
const lazyRetry = (importFn: () => Promise<{ default: React.ComponentType }>) =>
	lazy(() =>
		importFn().catch((err) => {
			const alreadyReloaded = sessionStorage.getItem("chunk-reload");
			if (!alreadyReloaded) {
				sessionStorage.setItem("chunk-reload", "1");
				window.location.reload();
				return new Promise<{ default: React.ComponentType }>(() => {});
			}
			sessionStorage.removeItem("chunk-reload");
			return new Promise<{ default: React.ComponentType }>(
				(resolve, reject) => {
					setTimeout(() => {
						importFn()
							.then(resolve)
							.catch(() => reject(err));
					}, 1000);
				},
			);
		}),
	);

// Layouts
const MainLayout = lazyRetry(() => import("@layout/MainLayout"));

// Pages
const DashboardPage = lazyRetry(
	() => import("@features/dashboard/presentation/page/DashboardPage"),
);
const AccountsPage = lazyRetry(
	() => import("@features/accounts/presentation/page/AccountsPage"),
);
const MessagingPage = lazyRetry(
	() => import("@features/messaging/presentation/page/MessagingPage"),
);
const ChatsPage = lazyRetry(
	() => import("@features/chats/presentation/page/ChatsPage"),
);
const InboxPage = lazyRetry(
	() => import("@features/inbox/presentation/page/InboxPage"),
);
const ScheduledPage = lazyRetry(
	() => import("@features/scheduled/presentation/page/ScheduledPage"),
);
const ContactsPage = lazyRetry(
	() => import("@features/contacts/presentation/page/ContactsPage"),
);
const GroupsPage = lazyRetry(
	() => import("@features/groups/presentation/page/GroupsPage"),
);
const ContactListsPage = lazyRetry(
	() => import("@features/contact-lists/presentation/page/ContactListsPage"),
);
const CannedResponsesPage = lazyRetry(
	() => import("@features/canned-responses/presentation/page/CannedResponsesPage"),
);
const AutoResponsesPage = lazyRetry(
	() => import("@features/auto-responses/presentation/page/AutoResponsesPage"),
);
const AIPromptsPage = lazyRetry(
	() => import("@features/ai-prompts/presentation/page/AIPromptsPage"),
);
const TemplatesPage = lazyRetry(
	() => import("@features/templates/presentation/page/TemplatesPage"),
);
const RemindersPage = lazyRetry(
	() => import("@features/reminders/presentation/page/RemindersPage"),
);
const LabelsPage = lazyRetry(
	() => import("@features/labels/presentation/page/LabelsPage"),
);
const AnalyticsPage = lazyRetry(
	() => import("@features/analytics/presentation/page/AnalyticsPage"),
);
const OutboxPage = lazyRetry(
	() => import("@features/outbox/presentation/page/OutboxPage"),
);
const ProfilePage = lazyRetry(
	() => import("@features/profile/presentation/page/ProfilePage"),
);
const ChangePasswordPage = lazyRetry(
	() => import("@features/auth/presentation/page/ChangePasswordPage"),
);
const ApiKeysPage = lazyRetry(
	() => import("@features/api-keys/presentation/page/ApiKeysPage"),
);
const TenantsPage = lazyRetry(
	() => import("@features/tenants/presentation/page/TenantsPage"),
);
const AIProvidersPage = lazyRetry(
	() => import("@features/ai-providers/presentation/page/AIProvidersPage"),
);
const TeamPage = lazyRetry(
	() => import("@features/team/presentation/page/TeamPage"),
);
const FlowsPage = lazyRetry(
	() => import("@features/flows/presentation/page/FlowsPage"),
);
const FlowBuilderPage = lazyRetry(
	() => import("@features/flows/presentation/page/FlowBuilderPage"),
);
const MediaLibraryPage = lazyRetry(
	() => import("@features/media-library/presentation/page/MediaLibraryPage"),
);
const StatusPage = lazyRetry(
	() => import("@features/status/presentation/page/StatusPage"),
);
const TenantSettingsPage = lazyRetry(() =>
	import("@features/tenant/presentation/page/TenantSettingsPage").then((m) => ({
		default: m.TenantSettingsPage,
	})),
);

function AppRoutes() {
	return (
		<BrowserRouter>
			<Suspense fallback={<LoadingScreen />}>
				<Routes>
					{/* Public routes */}
					<Route path="/login" element={<LoginPage />} />

					{/* Protected routes */}
					<Route element={<ProtectedRoute />}>
						<Route path="/dashboard" element={<MainLayout />}>
							<Route index element={<DashboardPage />} />
							<Route path="accounts" element={<AccountsPage />} />
							<Route path="messages/send" element={<MessagingPage />} />
							<Route path="inbox" element={<InboxPage />} />
							<Route path="chats" element={<ChatsPage />} />
							<Route path="scheduled" element={<ScheduledPage />} />
							<Route path="status" element={<StatusPage />} />
							<Route path="contacts" element={<ContactsPage />} />
							<Route path="groups" element={<GroupsPage />} />
							<Route path="contact-lists" element={<ContactListsPage />} />
							<Route path="canned-responses" element={<CannedResponsesPage />} />
							<Route path="auto-responses" element={<AutoResponsesPage />} />
							<Route path="flows" element={<FlowsPage />} />
							<Route path="flows/:id" element={<FlowBuilderPage />} />
							<Route path="media-library" element={<MediaLibraryPage />} />
							<Route path="ai-prompts" element={<AIPromptsPage />} />
							<Route path="templates" element={<TemplatesPage />} />
							<Route path="reminders" element={<RemindersPage />} />
							<Route path="labels" element={<LabelsPage />} />
							<Route path="analytics" element={<AnalyticsPage />} />
							<Route path="outbox" element={<OutboxPage />} />
							<Route path="profile" element={<ProfilePage />} />
							<Route path="change-password" element={<ChangePasswordPage />} />
							<Route path="settings" element={<TenantSettingsPage />} />
							<Route path="team" element={<TeamPage />} />
							<Route element={<AdminRoute />}>
								<Route path="api-keys" element={<ApiKeysPage />} />
								<Route path="tenants" element={<TenantsPage />} />
								<Route path="admin/ai-providers" element={<AIProvidersPage />} />
							</Route>
						</Route>
					</Route>

					{/* Redirect root to dashboard */}
					<Route path="/" element={<Navigate to="/dashboard" replace />} />
					<Route path="*" element={<Navigate to="/dashboard" replace />} />
				</Routes>
			</Suspense>
		</BrowserRouter>
	);
}

export default AppRoutes;
